import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../../pages/facilitiesMaster/facilitiesMaster.scss";
import { NotFound } from "../../components/common/NotFound";
import PaginationComponent from "../common/PaginationComponent";
import SkeletonCustomerMasterGrid from "../common/skeletonCustomerMasterGrid";

const FacilityActionMenu = ({ item, index, menuRights, handleEdit, handleDelete, handleView }) => {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const actions = [
    menuRights?.canEdit === true && { label: "Edit", icon: "far fa-edit", onClick: () => handleEdit(item) },
    menuRights?.canDelete === true && { label: "Delete", icon: "far fa-trash-alt mr-1", onClick: () => handleDelete(item) },
    menuRights?.canView === true && { label: "View", icon: "far fa-eye mr-1", onClick: () => handleView(item) },
  ].filter(Boolean);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const menuRect = menuRef.current?.getBoundingClientRect();
    const menuWidth = menuRect?.width || 115;
    const menuHeight = menuRect?.height || actions.length * 31;
    const viewportPadding = 8;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const preferredTop = spaceBelow >= menuHeight + viewportPadding
      ? triggerRect.bottom + 4
      : triggerRect.top - menuHeight - 4;

    setPosition({
      top: Math.min(
        Math.max(viewportPadding, preferredTop),
        Math.max(viewportPadding, window.innerHeight - menuHeight - viewportPadding)
      ),
      left: Math.min(
        Math.max(viewportPadding, triggerRect.right - menuWidth),
        window.innerWidth - menuWidth - viewportPadding
      ),
    });
  }, [actions.length]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!triggerRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen]);

  if (!actions.length) return null;

  return (
    <div className="action-menu text-align">
      <button
        ref={triggerRef}
        type="button"
        className="action-icon"
        title="Actions"
        aria-label={`Facility actions for row ${index + 1}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <i className="fas fa-ellipsis-v" aria-hidden="true" />
      </button>
      {isOpen && typeof document !== "undefined" && createPortal(
        <div
          ref={menuRef}
          className="facility-action-menu-portal"
          role="menu"
          style={position}
        >
          <ul>
            {actions.map((action) => (
              <li key={action.label} role="none">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setIsOpen(false);
                    action.onClick();
                  }}
                >
                  <i className={action.icon} aria-hidden="true" /> {action.label}
                </button>
              </li>
            ))}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
};

const CompanyGrid = ({
  filteredResults,
  handleEdit,
  handleDelete,
  handleView,
  loading,
  menuRights,
}) => {
  const [count, setCount] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = count;

  const currentTableData = useMemo(() => {
    if (filteredResults?.length <= pageSize) setCurrentPage(1);
    else if (currentPage > Math.ceil(filteredResults?.length / pageSize)) {
      setCurrentPage(1);
    }

    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    return filteredResults?.slice(firstPageIndex, lastPageIndex) || [];
  }, [currentPage, filteredResults, count]);

  return (
    <>
      <div className="row company-table-row mb-4rem">
        <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12">
          {loading === true ? (
            <SkeletonCustomerMasterGrid rows={12} />
          ) : currentTableData.length > 0 ? (
            <div className="card mt-0 company-list-card">
              <div className="card-body">
                <div className="table-responsive company-table-responsive">
                  <table className="table table-borderless table-list company-table-list">
                    <thead>
                      <tr>
                        <th><div>FACILITY CODE</div></th>
                        <th><div>FACILITY NAME</div></th>
                        <th><div>COUNTRY</div></th>
                        <th><div>ADDRESS</div></th>
                        <th><div>DEFAULT</div></th>
                        <th><div>STATUS</div></th>
                        <th><div>ACTIONS</div></th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentTableData?.map((item, index) => (
                        <tr key={item?.id || index}>
                          <td
                            onClick={
                              menuRights?.canEdit === true
                                ? () => handleEdit(item)
                                : menuRights?.canView === true
                                  ? () => handleView(item)
                                  : undefined
                            }
                            className={
                              menuRights?.canEdit === true || menuRights?.canView === true
                                ? "clickable-cell"
                                : ""
                            }
                          >
                            <h4 title={item?.facilityCode}>{item?.facilityCode || "-"}</h4>
                          </td>
                          <td>
                            <h5 title={item?.facilityName}>{item?.facilityName || "-"}</h5>
                          </td>
                          <td>
                            <h5 title={item?.countryName}>{item?.countryName || "-"}</h5>
                          </td>
                          <td>
                            <h5 title={item?.address}>{item?.address || "-"}</h5>
                          </td>
                          <td>
                            <h5>{item?.isDefaultLabel || "No"}</h5>
                          </td>
                          <td>
                            <button
                              type="button"
                              className={item?.status === "Active" ? "btn active-btn" : "btn inactive-btn"}
                            >
                              {item?.status || "-"}
                            </button>
                          </td>
                          <td>
                            <FacilityActionMenu
                              item={item}
                              index={index}
                              menuRights={menuRights}
                              handleEdit={handleEdit}
                              handleDelete={handleDelete}
                              handleView={handleView}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <NotFound />
          )}
        </div>
      </div>

      {currentTableData.length > 0 && (
        <PaginationComponent
          count={count}
          setCount={setCount}
          pageSize={pageSize}
          currentPage={currentPage}
          filteredResults={filteredResults}
          setCurrentPage={setCurrentPage}
        />
      )}
    </>
  );
};

export default CompanyGrid;
